import { useState, type ReactNode } from 'react';
import { Chip } from '../../components/ui/Chip';
import { useToast } from '../../components/ui/Toast';
import {
  useSmsAccounts,
  useCreateSmsAccount,
  useRetestSmsAccount,
  useConfirmAuSenderId,
  useDeleteSmsAccount,
  useUpdateSmsAccount,
  useDialpadAvailability,
  useSetTwilioAuthToken,
  type SmsProvider,
  type SmsProviderAccount,
  type SmsNumber,
  type CreateSmsAccountPayload,
  type DialpadAvailability,
} from '../../api/sms-accounts.api';
import { useOrgUsers } from '../../api/org-users.api';
import { useOrgSettings, useSetSmsProvider } from '../../api/org-settings.api';
import { useAuthStore } from '../../stores/auth.store';
import { useCan } from '../../hooks/useSession';
import { SmsAccountLimits } from './SmsLimitsCards';

function errMsg(err: unknown): string {
  const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return e.response?.data?.message ?? e.response?.data?.error ?? e.message ?? 'Unknown error';
}

/**
 * SMS settings — Section 3 of the SMS addendum, provider-agnostic.
 *
 * BYO credentials for whichever SMS provider the org wants to use (Twilio
 * for MVP; MessageBird / Vonage / SendCentral slot in by adding their own
 * branch to the provider selector + a corresponding backend adapter).
 * Each row carries its `provider` discriminator so the UI can show
 * provider-specific labels + hints without baking provider knowledge into
 * the campaign / inbox surfaces.
 *
 * Strictly separate from email mailboxes — STOP/opt-outs from SMS do not
 * affect email and vice versa.
 */
export function SmsAccountsSection() {
  const { data, isLoading } = useSmsAccounts();
  const accounts = data?.data ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  // Engage ADMIN — gates Dialpad connect, linking, and the provider toggle.
  // UX only; the server enforces.
  const isAdmin = useCan('ADMIN');

  return (
    <Shell
      title="SMS provider accounts"
      subtitle="Organisation-level: these provider credentials are shared across your whole Engage workspace. Only one provider sends at a time: Twilio (a number bound per campaign) or Dialpad (each automation sends from its owner's linked Dialpad number). Strictly separate from email mailboxes: STOP and opt-outs from SMS do not affect email, and vice versa."
      action={<PrimaryBtn onClick={() => setModalOpen(true)}>+ Connect account</PrimaryBtn>}
    >
      {isAdmin && <SmsProviderToggle accounts={accounts} />}

      <div style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
        <strong style={{ color: 'var(--color-text-primary)' }}>AU sender-ID gate:</strong>{' '}
        texts from a new Twilio account to Australian recipients wait until you confirm the
        numbers are registered with an Alpha sender ID or have an approved AU
        regulatory bundle, and the campaign says why. Non-AU sends are unaffected. Dialpad texts go from your
        team's own Dialpad numbers, so they aren't gated.
      </div>

      {isLoading && <Muted>Loading accounts…</Muted>}

      {!isLoading && accounts.length === 0 && <EmptyState onAdd={() => setModalOpen(true)} />}

      {!isLoading && accounts.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {accounts.map((a) => <AccountRow key={a.id} account={a} />)}
        </ul>
      )}

      {modalOpen && <AddSmsAccountModal onClose={() => setModalOpen(false)} isAdmin={isAdmin} />}
    </Shell>
  );
}

/**
 * Admin control: pick the org's active SMS provider (only one sends at a time)
 * and, for Dialpad, the org-default fallback number used when a campaign owner
 * has no linked Dialpad number.
 */
function SmsProviderToggle({ accounts }: { accounts: SmsProviderAccount[] }) {
  const { data } = useOrgSettings();
  const settings = data?.data;
  const setProvider = useSetSmsProvider();
  const { addToast } = useToast();
  const active = settings?.active_sms_provider ?? 'TWILIO';
  const defaultNumberId = settings?.default_sms_number_id ?? '';
  const dialpadNumbers = accounts
    .filter((a) => a.provider === 'DIALPAD')
    .flatMap((a) => a.numbers.map((n) => ({ ...n, account_label: a.label })));

  const change = async (next: 'TWILIO' | 'DIALPAD', defaultId?: string | null) => {
    try {
      await setProvider.mutateAsync({
        active_sms_provider: next,
        ...(defaultId !== undefined ? { default_sms_number_id: defaultId } : {}),
      });
      addToast({ variant: 'success', title: `SMS provider set to ${next === 'DIALPAD' ? 'Dialpad' : 'Twilio'}` });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not change provider', description: errMsg(err) });
    }
  };

  return (
    <div style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-strong)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', backgroundColor: 'var(--color-surface-white)' }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
        <strong>Active SMS provider</strong>: only one provider sends at a time. Switching takes effect on the next send; the other provider stays connected but dormant.
      </div>
      <Field label="Provider" hint="Twilio binds a number per campaign. Dialpad sends each automation from its owner's Dialpad number, as linked in JobTetris.">
        <select
          value={active}
          onChange={(e) => {
            const next = e.target.value as 'TWILIO' | 'DIALPAD';
            change(next, next === 'DIALPAD' ? (defaultNumberId || null) : undefined);
          }}
          disabled={setProvider.isPending}
          style={inputStyle()}
        >
          <option value="TWILIO">Twilio</option>
          <option value="DIALPAD">Dialpad</option>
        </select>
      </Field>
      {active === 'DIALPAD' && (
        <Field label="Org-default fallback number" hint="Used when a campaign owner has no linked Dialpad number. If left as “none”, such a send exits with an error instead of going out from the wrong number.">
          <select
            value={defaultNumberId}
            onChange={(e) => change('DIALPAD', e.target.value || null)}
            disabled={setProvider.isPending}
            style={inputStyle()}
          >
            <option value="">None</option>
            {dialpadNumbers.map((n) => (
              <option key={n.id} value={n.id}>{n.e164} · {n.friendly_name ?? n.account_label}</option>
            ))}
          </select>
        </Field>
      )}
      {active === 'DIALPAD' && dialpadNumbers.length === 0 && (
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
          No Dialpad numbers yet. Turn on Dialpad from JobTetris with + Connect account. Senders are the people linked to a Dialpad number in JobTetris.
        </span>
      )}
    </div>
  );
}

function AccountRow({ account }: { account: SmsProviderAccount }) {
  const retest = useRetestSmsAccount();
  const confirmAu = useConfirmAuSenderId();
  const update = useUpdateSmsAccount(account.id);
  const del = useDeleteSmsAccount();
  const { addToast } = useToast();
  const isDialpad = account.provider === 'DIALPAD';

  const handleRetest = async () => {
    try {
      await retest.mutateAsync(account.id);
      addToast({ variant: 'success', title: 'Account re-tested', description: account.label });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Re-test failed', description: (err as Error).message });
    }
  };

  const handleConfirmAu = async () => {
    if (!window.confirm('Confirm you have registered an Australian Alpha sender ID or have an approved regulatory bundle. This unblocks SMS to AU recipients.')) return;
    try {
      await confirmAu.mutateAsync(account.id);
      addToast({ variant: 'success', title: 'AU sender ID confirmed' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not confirm', description: (err as Error).message });
    }
  };

  const handleToggleStatus = async () => {
    const next = account.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED';
    if (next === 'DISABLED' && !window.confirm('Disable this SMS account? In-flight SMS campaigns using its numbers will pause.')) return;
    try {
      await update.mutateAsync({ status: next });
      addToast({ variant: 'success', title: next === 'DISABLED' ? 'Account disabled' : 'Account re-enabled' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Update failed', description: (err as Error).message });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete SMS account "${account.label}"? Numbers + historical messages stay; the connection just won't be usable for new sends.`)) return;
    try {
      await del.mutateAsync(account.id);
      addToast({ variant: 'success', title: 'Account removed' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Delete failed', description: (err as Error).message });
    }
  };

  return (
    <li style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
              {account.label}
            </span>
            <ProviderChip provider={account.provider} />
            <StatusChip status={account.status} />
            {account.user_id ? (
              <Chip variant="idle">User-level</Chip>
            ) : (
              <Chip variant="live">Org-level</Chip>
            )}
          </div>
          <SmsAccountLimits account={account} />
          {account.last_error && (
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
              Last error: {account.last_error}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          {account.requires_sender_id_au && (
            <SecondaryBtn onClick={handleConfirmAu} disabled={confirmAu.isPending}>
              {confirmAu.isPending ? 'Confirming…' : 'Confirm AU sender ID'}
            </SecondaryBtn>
          )}
          <SecondaryBtn onClick={handleRetest} disabled={retest.isPending}>
            {retest.isPending ? 'Testing…' : 'Re-test'}
          </SecondaryBtn>
          <SecondaryBtn onClick={handleToggleStatus} disabled={update.isPending}>
            {account.status === 'DISABLED' ? 'Re-enable' : 'Disable'}
          </SecondaryBtn>
          <DangerBtn onClick={handleDelete} disabled={del.isPending}>Delete</DangerBtn>
        </div>
      </div>

      {account.needs_auth_token && <AddAuthToken account={account} />}

      {account.numbers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
            {isDialpad ? 'Dialpad numbers, as linked to people in JobTetris' : 'Phone numbers'}
          </span>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {account.numbers.map((n) => (
              <li key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>{n.e164}</span>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {n.friendly_name ?? '-'} · {n.country_code}
                </span>
                <WebhookChip status={n.webhook_status} />
                {isDialpad && <LinkedPerson number={n} />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

/**
 * Who texts from a Dialpad number. Linked in JobTetris and copied here hourly (JT1-1856), so it's
 * read-only. Campaigns text from their owner's number.
 */
function LinkedPerson({ number }: { number: SmsNumber }) {
  const { data: users } = useOrgUsers();
  const current = number.assigned_user_id;
  const u = current ? users?.find((x) => x.jt_user_id === current) : undefined;
  return (
    <Chip variant={current ? 'live' : 'idle'}>
      {current ? `→ ${u?.name ?? u?.email ?? 'user'}` : 'not in Engage yet'}
    </Chip>
  );
}

/* ── Add modal — provider-aware ──────────────────────────────────────────── */

const DIALPAD_LABEL = 'Dialpad (from JobTetris)';

export function AddSmsAccountModal({ onClose, isAdmin }: { onClose: () => void; isAdmin: boolean }) {
  const create = useCreateSmsAccount();
  const { addToast } = useToast();
  const myUserId = useAuthStore((s) => s.user?.user_id);
  const [label, setLabel] = useState('');
  const [provider, setProvider] = useState<SmsProvider>('TWILIO');
  const [scope, setScope] = useState<'org' | 'user'>('org');
  // Twilio creds.
  const [accountSid, setAccountSid] = useState('');
  const [keySid, setKeySid] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [auRegistered, setAuRegistered] = useState<'yes' | 'no'>('no');
  const isDialpad = provider === 'DIALPAD';
  // Dialpad uses the agency's connection in JobTetris, so check it's there (JT1-1856).
  const dialpad = useDialpadAvailability(isDialpad);
  const dialpadAvailability = dialpad.data?.data;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Dialpad is always org-level (the agency's connection). Twilio honours the
      // scope picker: user-level attributes the account to the current user;
      // org-level (null) is a shared account (admin-only server-side).
      let payload: CreateSmsAccountPayload;
      if (provider === 'TWILIO') {
        payload = {
          label: label.trim(),
          user_id: scope === 'user' ? (myUserId ?? null) : null,
          provider: 'TWILIO',
          au_sender_registered: auRegistered === 'yes',
          credentials: {
            account_sid: accountSid.trim(),
            api_key_sid: keySid.trim(),
            api_key_secret: keySecret.trim(),
            auth_token: authToken.trim(),
          },
        };
      } else if (provider === 'DIALPAD') {
        payload = { provider: 'DIALPAD', label: DIALPAD_LABEL, user_id: null };
      } else {
        throw new Error(`Unsupported provider ${provider}`);
      }
      await create.mutateAsync(payload);
      addToast(
        isDialpad
          ? { variant: 'success', title: 'Dialpad from JobTetris is on', description: 'Campaign texts go from each owner’s Dialpad number.' }
          : {
              variant: 'success',
              title: 'SMS account connected',
              description: auRegistered === 'yes'
                ? 'Numbers found and set up to receive replies.'
                : 'Numbers found. Texts to Australian numbers wait until you confirm the AU sender ID.',
            }
      );
      onClose();
    } catch (err) {
      const e = err as {
        response?: {
          data?: {
            message?: string;
            error?: string;
            details?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
          };
        };
        message?: string;
      };
      const fieldErrors = e.response?.data?.details?.fieldErrors;
      const fieldMsg = fieldErrors
        ? Object.entries(fieldErrors)
            .map(([k, v]) => `${k}: ${v?.join('; ')}`)
            .join(' · ')
        : null;
      addToast({
        variant: 'danger',
        title: 'Could not connect SMS account',
        description:
          fieldMsg ?? e.response?.data?.message ?? e.response?.data?.error ?? e.message ?? 'Unknown error',
      });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          width: 'min(560px, 92vw)', backgroundColor: 'var(--color-surface-white)',
          borderRadius: 'var(--radius-input)', padding: 'var(--space-5)',
          display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
          Connect an SMS account
        </h3>

        <Field label="Provider" hint={isAdmin ? 'Twilio is a per-recruiter BYO connection. Dialpad uses the agency’s Dialpad connection in JobTetris (admin only).' : 'Twilio is a per-recruiter BYO connection. Turning on Dialpad is admin-only.'}>
          <select aria-label="Provider" value={provider} onChange={(e) => setProvider(e.target.value as SmsProvider)} style={inputStyle()}>
            <option value="TWILIO">Twilio</option>
            {isAdmin && <option value="DIALPAD">{DIALPAD_LABEL}</option>}
          </select>
        </Field>

        {!isDialpad && (
          <Field label="Label" hint="What to show in the picker, e.g. 'Acme: main line'">
            <input aria-label="Label" value={label} onChange={(e) => setLabel(e.target.value)} required style={inputStyle()} />
          </Field>
        )}

        {isDialpad && (
          <DialpadFromJobTetris availability={dialpadAvailability} loading={dialpad.isLoading} />
        )}

        {provider === 'TWILIO' && (
          <>
            <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-relaxed)' }}>
              Use a scoped Twilio <strong>API key</strong> (not your master Auth Token). Engage encrypts your
              key + secret at rest and uses them only to send / receive SMS and read usage records.
            </p>
            <Field label="Account SID" hint="Starts with AC, 34 chars total. Find under Console → Account → Account Info.">
              <input aria-label="Account SID" value={accountSid} onChange={(e) => setAccountSid(e.target.value)} required placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={inputStyle()} />
            </Field>
            <Field label="API Key SID" hint="Starts with SK. Create one under Console → Account → API keys & tokens.">
              <input aria-label="API Key SID" value={keySid} onChange={(e) => setKeySid(e.target.value)} required placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={inputStyle()} />
            </Field>
            <Field label="API Key Secret" hint="Shown once on creation in Twilio. You won't see it again here.">
              <input aria-label="API Key Secret" type="password" value={keySecret} onChange={(e) => setKeySecret(e.target.value)} required style={inputStyle()} />
            </Field>
            <AuthTokenField value={authToken} onChange={setAuthToken} />
            <Field
              label="Are these numbers registered for Australian sending?"
              hint="Australian networks block business texts from unregistered senders. Until you say yes, texts to Australian numbers wait, and the campaign says why. Texts to other countries go as normal."
            >
              <select aria-label="Registered for Australian sending" value={auRegistered} onChange={(e) => setAuRegistered(e.target.value as 'yes' | 'no')} style={inputStyle()}>
                <option value="no">Not yet</option>
                <option value="yes">Yes: an Alpha sender ID or an approved AU regulatory bundle</option>
              </select>
            </Field>
          </>
        )}

        {provider !== 'DIALPAD' && (
          <Field label="Scope" hint="Org-level accounts can be selected by anyone in the org. User-level stays with you.">
            <select value={scope} onChange={(e) => setScope(e.target.value as 'org' | 'user')} style={inputStyle()}>
              <option value="org">Org-level (shared)</option>
              <option value="user">User-level (just me)</option>
            </select>
          </Field>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
          <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
          <PrimaryBtn type="submit" disabled={create.isPending || (isDialpad && !dialpadAvailability?.available)}>
            {create.isPending ? 'Connecting…' : isDialpad ? 'Use Dialpad from JobTetris' : 'Connect'}
          </PrimaryBtn>
        </div>
      </form>
    </div>
  );
}

/** The Twilio Auth Token. Twilio signs replies and delivery updates with it (JT1-1860). */
function AuthTokenField({ value, onChange, ariaLabel = 'Auth Token' }: { value: string; onChange: (v: string) => void; ariaLabel?: string }) {
  return (
    <Field label="Auth Token" hint="Under Console → Account → Account Info. Twilio signs replies and delivery updates with it, so Engage uses it only to check they really come from Twilio. Stored encrypted.">
      <input aria-label={ariaLabel} type="password" value={value} onChange={(e) => onChange(e.target.value)} required style={inputStyle()} />
    </Field>
  );
}

/** A Twilio account connected without its Auth Token: its replies are refused until it's added (JT1-1860). */
function AddAuthToken({ account }: { account: SmsProviderAccount }) {
  const save = useSetTwilioAuthToken();
  const { addToast } = useToast();
  const [token, setToken] = useState('');

  const submit = async () => {
    try {
      await save.mutateAsync({ id: account.id, auth_token: token.trim() });
      setToken('');
      addToast({ variant: 'success', title: 'Auth Token saved', description: 'Replies and delivery updates from Twilio now come through.' });
    } catch (err) {
      addToast({ variant: 'danger', title: 'Could not save the Auth Token', description: errMsg(err) });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', padding: 'var(--space-3)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-warning)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
        Replies and delivery updates from Twilio are refused until you add this account&apos;s Auth Token. Twilio signs them with it.
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <AuthTokenField value={token} onChange={setToken} ariaLabel={`Auth Token for ${account.label}`} />
        <PrimaryBtn onClick={submit} disabled={save.isPending || !token.trim()}>
          {save.isPending ? 'Saving…' : 'Save Auth Token'}
        </PrimaryBtn>
      </div>
    </div>
  );
}

/** What turning on Dialpad from JobTetris does, or why it can't be turned on yet (JT1-1856). */
function DialpadFromJobTetris({ availability, loading }: { availability?: DialpadAvailability; loading: boolean }) {
  const text = { margin: 0, fontSize: 'var(--text-xs)', lineHeight: 'var(--leading-relaxed)' } as const;
  return (
    <>
      <p style={{ ...text, color: 'var(--color-text-muted)' }}>
        Engage texts through your agency&apos;s Dialpad connection in JobTetris, so there&apos;s nothing to enter here.
        Each campaign texts from its owner&apos;s Dialpad number, as linked in JobTetris, and every text shows once on
        the JobTetris timeline. Replies and STOPs come back to Engage&apos;s inbox.
      </p>
      {loading && <p style={{ ...text, color: 'var(--color-text-muted)' }}>Checking Dialpad in JobTetris…</p>}
      {availability?.available && (
        <p style={{ ...text, color: 'var(--color-text-primary)' }}>
          {availability.senders === 1
            ? '1 person is linked to a Dialpad number in JobTetris.'
            : `${availability.senders} people are linked to a Dialpad number in JobTetris.`}
        </p>
      )}
      {availability && !availability.available && (
        <p style={{ ...text, color: 'var(--color-warning)' }}>
          {availability.reason ?? "Dialpad isn't available from JobTetris."}
        </p>
      )}
    </>
  );
}

/* ── Small reusable building blocks ──────────────────────────────────────── */

function Shell({ title, subtitle, action, children }: { title: string; subtitle: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', padding: 'var(--card-padding)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)' }}>{title}</h2>
          <p style={{ margin: '2px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{subtitle}</p>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{ padding: 'var(--space-6)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-input)', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', alignItems: 'center' }}>
      <span>No SMS accounts connected yet.</span>
      <PrimaryBtn onClick={onAdd}>+ Connect account</PrimaryBtn>
    </div>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{children}</p>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>}
    </label>
  );
}

function ProviderChip({ provider }: { provider: SmsProvider }) {
  return <Chip variant="scheduled">{provider}</Chip>;
}

function StatusChip({ status }: { status: SmsProviderAccount['status'] }) {
  switch (status) {
    case 'ACTIVE':              return <Chip variant="live">Active</Chip>;
    case 'REQUIRES_SENDER_ID':  return <Chip variant="warming">AU gated</Chip>;
    case 'UNVERIFIED':          return <Chip variant="idle">Unverified</Chip>;
    case 'DISABLED':            return <Chip variant="idle">Disabled</Chip>;
    case 'ERROR':               return <Chip variant="error">Error</Chip>;
  }
}

function WebhookChip({ status }: { status: 'PENDING' | 'CONFIGURED' | 'ERROR' }) {
  if (status === 'CONFIGURED') return <Chip variant="live">webhook OK</Chip>;
  if (status === 'PENDING')    return <Chip variant="idle">webhook pending</Chip>;
  return <Chip variant="error">webhook error</Chip>;
}

function inputStyle(): React.CSSProperties {
  return { height: 'var(--btn-height)', padding: '0 var(--space-3)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', backgroundColor: 'var(--color-surface-white)' };
}

function PrimaryBtn({ children, onClick, type, disabled }: { children: ReactNode; onClick?: () => void; type?: 'button' | 'submit'; disabled?: boolean }) {
  return <button type={type ?? 'button'} onClick={onClick} disabled={disabled} style={{ height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', border: 'none', backgroundColor: disabled ? 'var(--color-surface-muted)' : 'var(--color-action-primary)', color: disabled ? 'var(--color-text-muted)' : 'var(--color-action-primary-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', cursor: disabled ? 'not-allowed' : 'pointer' }}>{children}</button>;
}

function SecondaryBtn({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return <button type="button" onClick={onClick} disabled={disabled} style={{ height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', border: '1px solid var(--color-border-default)', backgroundColor: 'transparent', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', cursor: disabled ? 'not-allowed' : 'pointer' }}>{children}</button>;
}

function DangerBtn({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return <button type="button" onClick={onClick} disabled={disabled} style={{ height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', border: '1px solid var(--color-warning)', backgroundColor: 'transparent', color: 'var(--color-warning)', fontFamily: 'var(--font-sans)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', cursor: disabled ? 'not-allowed' : 'pointer' }}>{children}</button>;
}
